import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions as actionsUserCards } from 'appRedux/modules/UserCards';
import { Message } from 'components';
import {
	initialiseSDK,
	getCreditCardForm,
	getPrime,
	validateCard,
} from 'services/tappay';
import { Cookies } from 'utils';

/** ---------------------------------------------------------------------------------------------
 * Props
 * @type {string} display                      Display state
 *  - block: Display
 *  - none: Hide
 *
 * @type {string} source                       Source of the credit card form request
 *  - order: Payment page
 *  - profile: Profile page
 *
 * @type {boolean} cardBound                   Whether the card is bound
 *  - false: Not initialized yet
 *  - null: No card bound
 *  - object: Card bound
 *   @property {string} bindCardID Bind card ID
 *   @property {string} bincode Bincode (first six digits)
 *   @property {string} lastfour Last four digits
 *   @property {string} trade_type Trade type (must be tappay)
 */
const TapPayForm = ({
	display,
	source = 'order',
	cardBound,
}) => {
	/** ---------------------------------------------------------------------------------------------
	 * Basic
	 */
	const dispatch = useDispatch();

	/** ---------------------------------------------------------------------------------------------
	 * Redux
	 * @type {undefined | string | null} three_d_url    URL of the 3D secure received from Backend
	 * - undefined: Not initialized yet
	 * - string: URL of the 3D secure
	 * - null: No 3D secure
	 *
	 * @type {undefined | number} tap_pay_status    Status for non-3D secure transactions
	 * - undefined: Not initialized yet
	 * - number: Status (1: success)
	 *
	 * @type {boolean} tap_pay_card_add_loading    Loading state for addCard button
	 *  - true: Loading
	 *  - false: Not loading
	 *
	 * @type {string} colour_theme    Colour theme of the website
	 */
	const {
		three_d_url,
		tap_pay_status,
		tap_pay_card_add_loading,
		colour_theme,
	} = useSelector(state => ({
		three_d_url: state.UserCards.three_d_url,
		tap_pay_status: state.UserCards.tap_pay_status,
		tap_pay_card_add_loading: state.UserCards.tap_pay_card_add_loading,
		colour_theme: state.ColourTheme.colour_theme,
	}));

	/** ---------------------------------------------------------------------------------------------
	 * State
	 * @type {object} cardStatus
	 *  @property {boolean} can_get_prime     Whether the card info is valid to get prime
	 *  @property {number} number             Number of the card
	 *  @property {number} expiry             Expiry of the card
	 *  @property {number} ccv                CCV of the card
	 *   - 0: Valid
	 *   - 1: Other field(s) empty
	 *   - 2: Error
	 *   - 3: Typing
	 */
	const [cardStatus, setCardStatus] = useState({
		can_get_prime: false,
		number: 1,
		expiry: 1,
		ccv: 1,
	});

	/** ---------------------------------------------------------------------------------------------
	 * Function
	 * @type {function} addCard     Bind the card
	 */
	const addCard = () => {
		if (!cardStatus.can_get_prime) {
			return;
		}

		dispatch(actionsUserCards.addTapPayCardLoading(true));
		getPrime(result => {
			if (result.success) {
				dispatch(
					actionsUserCards.addTapPayCardRequest({
						...result,
						phone: '02-27473447',
						source: source,
					})
				);
			} else {
				console.log('Failed to get prime:', result.error);
			}
		});
	};

	/** ---------------------------------------------------------------------------------------------
	 * useEffect
	 */
	useEffect(() => {
		// 1. Check if SDK loaded and update form when display or colour_theme changes
		const isInitialised = initialiseSDK();

		if (isInitialised && display === 'block') {
			getCreditCardForm(colour_theme);
		}
	}, [display, colour_theme]);

	useEffect(() => {
		// 2. Validation handler
		validateCard(update => {
			setCardStatus({ ...update });
		});
	}, []);

	useEffect(() => {
		// 3. Redirect to 3D secure
		if (three_d_url) {
			Cookies.remove('redirect_path', { path: '/' });
			window.location.replace(three_d_url);
		}

		// 4. Non-3D secure transactions
		if (three_d_url === null) {
			// Success
			if (tap_pay_status === 1) {
				// Get the credit card bound request
				dispatch(actionsUserCards.getUserCardRequest());
				// Show success message
				Message.SmallBox({
					title: `<i class='fa fa-check-circle txt-white' aria-hidden='true'></i> 卡片綁定完成！`,
					content: '立即前往結帳，完成您的購買。',
					timeout: 8000,
				});
			}
		}
	}, [dispatch, tap_pay_status, three_d_url]);

	// ---------------------------------------------------------------------------------------------

	return (
		<div className={`tappay-form d-${display}`}>
			<div
				className={`tpfield-container ${
					cardBound ? 'invisible height-0' : 'visible'
				}`}
			>
				<label htmlFor="card-number">信用卡號：</label>

				{/* Field container */}
				<div id="card-number" className="tpfield" />

				{cardStatus.number === 0 && (
					<i className="invalid txt-green fa fa-check-circle" />
				)}

				{cardStatus.number === 2 && (
					<i className="invalid txt-red fa fa-times-circle" />
				)}
			</div>

			<div
				className={`row ${
					cardBound ? 'invisible height-0' : 'visible'
				}`}
			>
				<div className="tpfield-container col-sm-12 col-lg-6">
					<label htmlFor="card-expiration-date">有效期限：</label>

					{/* Field container */}
					<div id="card-expiration-date" className="tpfield" />

					{cardStatus.expiry === 0 && (
						<i className="invalid txt-green fa fa-check-circle" />
					)}

					{cardStatus.expiry === 2 && (
						<i className="invalid txt-red fa fa-times-circle" />
					)}
				</div>

				<div className="tpfield-container col-sm-12 col-lg-6">
					<label htmlFor="card-ccv">安全碼：</label>

					{/* Field container */}
					<div id="card-ccv" className="tpfield" />

					{cardStatus.ccv === 0 && (
						<i className="invalid txt-green fa fa-check-circle" />
					)}

					{cardStatus.ccv === 2 && (
						<i className="invalid txt-red fa fa-times-circle" />
					)}
				</div>
			</div>

			<div className="btn-container">
				{!cardBound && (
					<button
						type="button"
						className={`ua-btn default ${
							!cardStatus.can_get_prime ||
							tap_pay_card_add_loading
								? 'disabled'
								: ''
						}`}
						disabled={
							!cardStatus.can_get_prime ||
							tap_pay_card_add_loading
						}
						onClick={addCard}
					>
						{tap_pay_card_add_loading && (
							<span
								className="spinner-border spinner-border-sm mr-2"
								role="status"
								aria-hidden="true"
							></span>
						)}
						綁定
					</button>
				)}
			</div>
		</div>
	);
};

export { TapPayForm };
